// Typed client for Lomi Live — the one-shot live mock-exam competition.
// Server: student-agent-api routers live_event_router / live_exam_router /
// live_leaderboard_router at /live/*. Competition semantics: payload carries NO
// answers, the server owns the clock + grading, no per-question feedback.

import api from './api';

export interface LiveSlot {
  subject: string;
  start_eat: string;
  budget_min: number;
  phase?: 'upcoming' | 'open' | 'closed';
  start_utc?: string | null;
  close_utc?: string | null;
}

export interface LiveCountdown {
  kind: 'pre' | 'next' | 'live' | 'awaiting_reveal' | 'completed';
  subject?: string;
  target_utc?: string;
}

export interface LiveRegistration {
  stream: string;
  subjects: string[];
  school_name: string | null;
}

export interface LiveEventInfo {
  slug: string;
  name: string;
  status: 'draft' | 'registration_open' | 'live' | 'completed' | 'archived';
  event_date: string | null;
  starts_at_utc?: string | null;
  now_utc?: string | null;
  countdown?: LiveCountdown;
  subjects: string[];
  streams: Record<string, string[]>;
  slots: LiveSlot[];
  questions_per_subject: number;
  timezone: string;
  registered_count: number;
  prizes_teaser?: string;
  prizes_teaser_am?: string;
  registration: LiveRegistration | null;
  my_attempts: Record<string, { status: 'in_progress' | 'submitted'; correct?: number; total?: number }>;
}

export interface LiveOption {
  label: string;
  text: string;
}

export interface LiveQuestion {
  seq: number;
  stem: string;
  topic: string | null;
  options: LiveOption[];
  media: unknown[];
}

export interface LivePayload {
  slug: string;
  subject: string;
  questions: LiveQuestion[];
  budget_seconds: number;
}

export interface LiveStartResponse {
  status: 'in_progress' | 'submitted';
  subject: string;
  attempt_id?: string;
  started_at?: string;
  budget_seconds?: number;
  elapsed_ms?: number;
  remaining_seconds?: number;
  correct_count?: number;
  total_questions?: number;
  total_time_ms?: number;
  already_done?: boolean;
  saved?: Record<string, string>; // resume: seq -> this student's display label
}

export interface LiveSubmitAnswer {
  seq: number;
  chosen: string | null;
  time_spent_ms?: number;
}

export interface LiveSubmitResponse {
  submitted: boolean;
  subject: string;
  correct_count?: number; // present only once the subject's slot has closed
  total_questions?: number;
  total_time_ms?: number;
  late: boolean;
  idempotent?: boolean;
  submitted_at?: string; // server submit timestamp (ISO); shown on the result screen
}

export interface LiveLBEntry {
  rank: number;
  name?: string;
  school?: string | null;
  correct?: number;
  time_ms?: number;
  students?: number;
  avg_correct?: number;
  total_correct?: number;
}

export interface LiveBoard {
  scope: string;
  subject?: string;
  stream?: string;
  subjects?: string[];
  entries: LiveLBEntry[];
}

export interface LiveMyRanks {
  scope: 'me';
  subjects: { subject: string; correct: number; time_ms: number; rank: number; of: number }[];
}

export interface LiveRegisterRequest {
  stream?: string;
  subjects?: string[];
  school_name?: string;
  referral_source?: string;
  name?: string;
}

export interface LiveReviewOption {
  label: string;
  text: string;
  is_correct: boolean;
  is_chosen: boolean;
}

export interface LiveReviewItem {
  seq: number;
  stem: string;
  options: LiveReviewOption[];
  explanation: string | null;
  your_answer: string | null;
  correct_answer: string;
  is_correct: boolean;
}

export interface LiveReview {
  subject: string;
  correct_count: number;
  total: number;
  items: LiveReviewItem[];
}

export interface LiveReveal {
  event: { slug: string; name: string; status: string };
  final: boolean;
  stats: { participants: number; attempts: number; submitted: number };
  subjects: Record<string, LiveLBEntry[]>;
  overall: LiveLBEntry[];
  grand: { natural: LiveLBEntry[]; social: LiveLBEntry[] };
  school: LiveLBEntry[];
}

export interface LiveMyResult {
  ready: boolean;
  status?: string;
  disqualified?: boolean;
  name?: string;
  overall?: { rank: number; of: number; correct: number; time_ms: number } | null;
  subjects?: { subject: string; correct: number; total?: number; of?: number; rank: number; cash: number; time_ms?: number }[];
  stream?: { name: string; rank: number; of: number; correct: number } | null;
  school?: { rank: number; avg_correct: number; students: number } | null;
  prize?: { total: number; lines: { label: string; cash: number }[] } | null;
}

export const liveExamAPI = {
  event: async (): Promise<LiveEventInfo> => {
    const r = await api.get<LiveEventInfo>('/live/event');
    return r.data;
  },

  register: async (body: LiveRegisterRequest = {}): Promise<any> => {
    const r = await api.post('/live/register', body);
    return r.data;
  },

  schoolSuggest: async (q: string): Promise<string[]> => {
    const r = await api.get<{ suggestions: string[] }>('/live/school-suggest', { params: { q } });
    return r.data.suggestions;
  },

  payload: async (subject: string): Promise<LivePayload> => {
    const r = await api.get<LivePayload>(`/live/payload/${encodeURIComponent(subject)}`);
    return r.data;
  },

  start: async (subject: string): Promise<LiveStartResponse> => {
    const r = await api.post<LiveStartResponse>(`/live/start/${encodeURIComponent(subject)}`, {});
    return r.data;
  },

  submit: async (subject: string, answers: LiveSubmitAnswer[]): Promise<LiveSubmitResponse> => {
    const r = await api.post<LiveSubmitResponse>(
      `/live/submit/${encodeURIComponent(subject)}`,
      { answers },
    );
    return r.data;
  },

  save: async (subject: string, answers: LiveSubmitAnswer[]): Promise<{ saved: boolean }> => {
    const r = await api.post(`/live/save/${encodeURIComponent(subject)}`, { answers });
    return r.data;
  },

  board: async (scope: string, n = 20): Promise<LiveBoard> => {
    const r = await api.get<LiveBoard>(`/live/leaderboard/${scope}`, { params: { n } });
    return r.data;
  },

  myRanks: async (): Promise<LiveMyRanks> => {
    const r = await api.get<LiveMyRanks>('/live/leaderboard/me');
    return r.data;
  },

  review: async (subject: string): Promise<LiveReview> => {
    const r = await api.get<LiveReview>(`/live/review/${encodeURIComponent(subject)}`);
    return r.data;
  },

  reveal: async (n = 10): Promise<LiveReveal> => {
    const r = await api.get<LiveReveal>('/live/reveal', { params: { n } });
    return r.data;
  },

  myResult: async (): Promise<LiveMyResult> => {
    const r = await api.get<LiveMyResult>('/live/my-result');
    return r.data;
  },
};
